import React from 'react';
import {Link} from 'react-router-dom';
import PropTypes from 'prop-types';
import {Formik} from 'formik';

import {gettext, ngettext} from 'utils/text';
import Section from 'components/layouts/Section';
import {configType} from 'propTypes/config';
import {productType} from 'propTypes/products';
import {USER_LANG} from 'utils/locale';
import {QUALITY_CONTROL} from 'constants/production';
import {ORDERS} from 'constants/sales';
import ProductionQualityControlDecision from './ProductionQualityControlDecision';
import ProductionQualityControlOptions from './ProductionQualityControlOptions';

const EstimatedVsActualInfo = ({product}) => {
    const displayHours = value => `${value.toFixed(1).replace('.', ',')} ${ngettext('hour', 'hours', value)}`;

    const estimated = product.estimated_duration;
    const actual = product.actual_welding_time;
    const percentage = ((actual / estimated) * 100) - 100;

    const actualDisplay = displayHours(actual);
    let estimatedDisplay;
    let percentageDisplay;

    if (estimated !== null) {
        estimatedDisplay = displayHours(estimated);
        percentageDisplay = `${percentage.toFixed(0)} %`;
    } else {
        estimatedDisplay = '-';
        percentageDisplay = '-';
    }

    return (
        <div className="mb-4">
            <div className="row mw-400">
                <div className="col-6 text-primary">{gettext("Estimated time:")}</div>
                <div className="col-6 text-primary">{estimatedDisplay}</div>
                <div className="col-6 text-primary">{gettext("Actual time:")}</div>
                <div className="col-6 text-primary">{actualDisplay}</div>
                <div className="col-6 text-primary">{gettext("Difference:")}</div>
                <div className="col-6 text-primary">{percentageDisplay}</div>
            </div>
        </div>
    );
};

EstimatedVsActualInfo.propTypes = {
    product: productType.isRequired,
};


const FormLayout = (product, config) => {
    const Form = (
        {values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue},
    ) => {
        let nonFieldErrors = null;
        if (errors.nonFieldErrors) {
            nonFieldErrors = errors.nonFieldErrors.map((error, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="alert alert-danger" key={`non-field-error-${i}`}>{error}</div>
            ));
        }

        const commonFormikProps = {values, errors, touched, handleChange, handleBlur};

        return (
            <Section title={gettext('Quality control')}>
                <form onSubmit={handleSubmit}>
                    {nonFieldErrors}

                    {product.order.status === ORDERS.STATUS.WELDING &&
                    <div>
                        <EstimatedVsActualInfo product={product} />

                        <ProductionQualityControlOptions
                            title={gettext("Estimated welding time vs actual welding time")}
                            name="estimated_vs_actual"
                            options={[
                                {
                                    // Translator: xgettext:no-javascript-format
                                    label: gettext('Less than 10% over estimated welding time'),
                                    value: QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_LESS_THAN_10,
                                    percentage: config.EVA_LESS_THAN_10_SALARY_PERCENTAGE,
                                },
                                {
                                    // Translator: xgettext:no-javascript-format
                                    label: gettext('10%-20% over estimated welding time'),
                                    value: QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_10_TO_20,
                                    percentage: config.EVA_10_TO_20_SALARY_PERCENTAGE,
                                },
                                {
                                    // Translator: xgettext:no-javascript-format
                                    label: gettext('21%-30% over estimated welding time'),
                                    value: QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_21_TO_30,
                                    percentage: config.EVA_21_TO_30_SALARY_PERCENTAGE,
                                },
                                {
                                    // Translator: xgettext:no-javascript-format
                                    label: gettext('31%-40% over estimated welding time'),
                                    value: QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_31_TO_40,
                                    percentage: config.EVA_31_TO_40_SALARY_PERCENTAGE,
                                },
                                {
                                    // Translator: xgettext:no-javascript-format
                                    label: gettext('More than 40% over estimated welding time'),
                                    value: QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_MORE_THAN_40,
                                    percentage: config.EVA_MORE_THAN_40_SALARY_PERCENTAGE,
                                },
                            ]}
                            disabled={!product.is_batch_welding_finished}
                            {...commonFormikProps}
                        />
                        <ProductionQualityControlOptions
                            title={gettext("Tolerance")}
                            name="tolerance"
                            options={[
                                {
                                    label: gettext('OK'),
                                    value: QUALITY_CONTROL.TOLERANCE_OK,
                                    percentage: config.TOLERANCE_OK_SALARY_PERCENTAGE,
                                },
                                {
                                    label: gettext('Not OK'),
                                    value: QUALITY_CONTROL.TOLERANCE_NOT_OK,
                                    percentage: config.TOLERANCE_NOT_OK_SALARY_PERCENTAGE,
                                },
                            ]}
                            {...commonFormikProps}
                        />
                        <ProductionQualityControlOptions
                            title={gettext("Quality")}
                            name="welding_quality"
                            options={[
                                {
                                    label: gettext('Good'),
                                    value: QUALITY_CONTROL.WELDING_QUALITY_GOOD,
                                    percentage: config.WELDING_QUALITY_GOOD_SALARY_PERCENTAGE,
                                },
                                {
                                    label: gettext('Neutral'),
                                    value: QUALITY_CONTROL.WELDING_QUALITY_NEUTRAL,
                                    percentage: config.WELDING_QUALITY_NEUTRAL_SALARY_PERCENTAGE,
                                },
                                {
                                    label: gettext('Bad'),
                                    value: QUALITY_CONTROL.WELDING_QUALITY_BAD,
                                    percentage: config.WELDING_QUALITY_BAD_SALARY_PERCENTAGE,
                                },
                            ]}
                            {...commonFormikProps}
                        />
                    </div>
                    }

                    <ProductionQualityControlDecision
                        title={gettext("Decision")}
                        product={product}
                        setFieldValue={setFieldValue}
                        {...commonFormikProps}
                    />
                    <div className="form-group">
                        <button
                            className="btn btn-primary mr-2"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {gettext('Submit')}
                        </button>
                        <Link className="btn btn-outline-danger" to={`/${USER_LANG}/production`}>
                            {gettext('Cancel')}
                        </Link>
                    </div>
                </form>
            </Section>
        );
    };

    Form.propTypes = {
        values: PropTypes.shape({
            estimated_vs_actual: PropTypes.string,
            tolerance: PropTypes.string,
            welding_quality: PropTypes.string,
            action: PropTypes.string.isRequired,
            comment: PropTypes.string.isRequired,
        }).isRequired,
        errors: PropTypes.shape({
            estimated_vs_actual: PropTypes.bool,
            tolerance: PropTypes.string,
            welding_quality: PropTypes.string,
            action: PropTypes.string,
            comment: PropTypes.string,
        }).isRequired,
        touched: PropTypes.shape({
            estimated_vs_actual: PropTypes.bool,
            tolerance: PropTypes.bool,
            welding_quality: PropTypes.bool,
            action: PropTypes.bool,
            comment: PropTypes.bool,
        }).isRequired,
        handleChange: PropTypes.func.isRequired,
        handleBlur: PropTypes.func.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        isSubmitting: PropTypes.bool.isRequired,
        setFieldValue: PropTypes.func.isRequired,
    };

    return Form;
};

FormLayout.propTypes = {
    product: productType,
    config: configType.isRequired,
};

class ProductQualityControlForm extends React.Component {
    static propTypes = {
        product: productType,
        config: configType.isRequired,
        onSubmit: PropTypes.func.isRequired,
    };

    static defaultProps = {
        product: null,
    };

    mapToInitialRadioOption = (estimated, actual) => {
        const percentage = ((actual / estimated) * 100) - 100;

        if (percentage < 10) {
            return QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_LESS_THAN_10;
        }
        if (percentage <= 20) {
            return QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_10_TO_20;
        }
        if (percentage <= 30) {
            return QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_21_TO_30;
        }
        if (percentage <= 40) {
            return QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_31_TO_40;
        }

        return QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_MORE_THAN_40;
    };

    validateForm = (values, isWeldingQA) => {
        const {product} = this.props;
        const errors = {};
        const requiredMessage = gettext('This field is required.');

        if (isWeldingQA) {
            const validEstimateVsActual = [
                QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_LESS_THAN_10,
                QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_10_TO_20,
                QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_21_TO_30,
                QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_31_TO_40,
                QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_MORE_THAN_40,
            ];
            if (
                !validEstimateVsActual.includes(values.estimated_vs_actual)
                && product.is_batch_welding_finished
            ) {
                errors.estimated_vs_actual = requiredMessage;
            }
            if (![QUALITY_CONTROL.TOLERANCE_OK,
                QUALITY_CONTROL.TOLERANCE_NOT_OK].includes(values.tolerance)) {
                errors.tolerance = requiredMessage;
            }
            if (![QUALITY_CONTROL.WELDING_QUALITY_GOOD, QUALITY_CONTROL.WELDING_QUALITY_NEUTRAL,
                QUALITY_CONTROL.WELDING_QUALITY_BAD].includes(values.welding_quality)) {
                errors.welding_quality = requiredMessage;
            }
        }

        if (![QUALITY_CONTROL.APPROVE,
            QUALITY_CONTROL.REJECT].includes(values.action)) {
            errors.action = requiredMessage;
        }
        if (values.action === QUALITY_CONTROL.REJECT && !values.nextStatus) {
            errors.nextStatus = requiredMessage;
        }
        if (values.action === QUALITY_CONTROL.REJECT && !values.comment) {
            errors.comment = requiredMessage;
        }

        return errors;
    };

    render() {
        const {product} = this.props;
        const isWeldingQA = product.order.status === ORDERS.STATUS.WELDING;
        const initialEstimatedVsActualRadioChoice = isWeldingQA && product.is_batch_welding_finished
            ? this.mapToInitialRadioOption(
                product.estimated_duration,
                product.actual_welding_time,
            )
            : '';

        const initial = {
            comment: '',
            estimated_vs_actual: initialEstimatedVsActualRadioChoice,
            tolerance: '',
            welding_quality: '',
            action: '',
        };

        let feedback = product.quality_control_feedback;
        if (feedback.length && isWeldingQA) {
            feedback = feedback
                .filter(f => f.product_status === ORDERS.STATUS.WELDING)
                .sort((a, b) => (b.id - a.id))[0];

            initial.tolerance = feedback.tolerance;
            initial.welding_quality = feedback.welding_quality;
        }


        return (
            <Formik
                render={FormLayout(product, this.props.config)}
                initialValues={{
                    ...initial,
                    nextStatus: product.previous_statuses[0],
                }}
                onSubmit={this.props.onSubmit}
                validate={values => this.validateForm(values, isWeldingQA)}
            />
        );
    }
}

export default ProductQualityControlForm;



// WEBPACK FOOTER //
// ./src/components/ProductQualityControlForm.js